import {tokens} from '@pleo-io/telescope'
import * as ReachDialog from '@reach/dialog'
import VisuallyHidden from '@reach/visually-hidden'
import React, {ReactNode} from 'react'
import styled, {css} from 'styled-components'
import {v4 as uuidv4} from 'uuid'

import Image from '@helios/shared/components/image'
import {Close} from '@helios/shared/images/icons/close'
import {useGlobalStrings} from '@helios/shared/providers/global-strings'
import {gutters} from '@helios/shared/styles/grid'
import {Text} from '@helios/shared/styles/typography'
import {colors, radii, zIndex} from '@helios/shared/theme'
import ImageSource from '@helios/shared/types/image'
import mq from '@helios/shared/utils/media-query'

type Props = {
    isOpen?: boolean
    onDismiss?: () => void
    title: string
    subtitle?: string
    image?: ImageSource
    children: ReactNode
}

export const Modal = ({children, isOpen, onDismiss, title, subtitle, image}: Props) => {
    const globalStrings = useGlobalStrings()
    const labelId = uuidv4()

    return (
        <DialogOverlay isOpen={isOpen} onDismiss={onDismiss}>
            <DialogContent aria-labelledby={labelId}>
                <CloseWrapper>
                    <CloseButton onClick={onDismiss}>
                        <VisuallyHidden>{globalStrings.close_button_text}</VisuallyHidden>
                        <CloseIcon />
                    </CloseButton>
                </CloseWrapper>

                <IllustrationAndTitles>
                    {image && <Illustration image={image} width={80} aria-hidden />}

                    <div>
                        <Text as="h2" id={labelId} variant="title-base" center>
                            {title}
                        </Text>

                        {subtitle && (
                            <Text as="h3" variant="paragraph-base" color="muted" center>
                                {subtitle}
                            </Text>
                        )}
                    </div>
                </IllustrationAndTitles>

                <div>{children}</div>
            </DialogContent>
        </DialogOverlay>
    )
}
type ModalContentProps = {
    children: ReactNode
}

export const ModalContent = ({children}: ModalContentProps) => (
    <ContentWrapper>{children}</ContentWrapper>
)

type ModalActionsProps = {
    children: ReactNode
}

export const ModalActions = ({children}: ModalActionsProps) => (
    <ActionsWrapper $numItems={React.Children.toArray(children).filter(Boolean).length}>
        {children}
    </ActionsWrapper>
)

const mobileBreakpoint = 'xs'

const sectionPadding = css`
    padding: ${tokens.spacing20};

    ${mq.min(mobileBreakpoint)} {
        padding-right: ${tokens.spacing40};
        padding-left: ${tokens.spacing40};
    }
`

const CloseWrapper = styled.div`
    display: flex;
    width: 100%;
    padding: ${tokens.spacing10};
    justify-content: flex-end;
`

const CloseButton = styled.button`
    display: flex;
    width: 20px;
    height: 20px;
    padding: 0;
    cursor: pointer;
    background: none;
    border: none;
`

const CloseIcon = styled(Close).attrs({'aria-hidden': true})`
    width: 100%;
    height: 100%;
    object-fit: contain;
    color: ${tokens.shade800};
`

const DialogOverlay = styled(ReachDialog.DialogOverlay)`
    ${gutters}

    z-index: ${zIndex.modalOverlay};
    display: flex;
    padding-top: ${tokens.spacing24};
    padding-bottom: ${tokens.spacing24};
    flex-direction: column;
    background: ${tokens.shade900lighten50};
    align-items: center;
`

const DialogContent = styled(ReachDialog.DialogContent)`
    width: 100%;
    max-width: 520px;
    padding: 0;
    margin: 0;
    margin: auto;
    background: ${colors.background.default};
    border-radius: ${radii.arc8};
    box-shadow: ${tokens.shadowRaiseMedium};
`

const IllustrationAndTitles = styled.div`
    ${sectionPadding}

    display: grid;
    justify-items: center;
    row-gap: ${tokens.spacing8};
`

const Illustration = styled(Image)`
    width: 60px;
    height: 60px;
    object-fit: contain;

    ${mq.min(mobileBreakpoint)} {
        width: 80px;
        height: 80px;
    }
`

const ContentWrapper = styled.div`
    ${sectionPadding}

    display: grid;
    row-gap: ${tokens.spacing20};
`

type ActionsWrapperProps = {
    $numItems: number
}

const ActionsWrapper = styled.div<ActionsWrapperProps>`
    ${sectionPadding}

    display: grid;
    gap: ${tokens.spacing20};
    grid-auto-flow: row;
    grid-auto-columns: 1fr;
    width: 100%;

    ${mq.min(mobileBreakpoint)} {
        grid-auto-flow: column;
        justify-items: ${(props) => props.$numItems === 1 && 'center'};
    }
`
